import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Pattern {
  id: string
  name: string
  type: string
  support: number
  confidence: number
  lift: number
  items_count: number
  description: string
}

interface Association {
  id: string
  antecedent: string[]
  consequent: string[]
  support: number
  confidence: number
  lift: number
  conviction: number
}

interface Statistics {
  total_patterns: number
  active_mining_jobs: number
  total_associations: number
  avg_support: number
  avg_confidence: number
  total_items_analyzed: number
  total_transactions: number
  last_mining_run: string
}

export default function DataMiningPage() {
  const [patterns, setPatterns] = useState<Pattern[]>([])
  const [associations, setAssociations] = useState<Association[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'patterns' | 'associations'>('patterns')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [patternsRes, assocRes, statsRes] = await Promise.all([
        fetch('/api/datamining/patterns'),
        fetch('/api/datamining/associations'),
        fetch('/api/datamining/statistics')
      ])

      if (patternsRes.ok) setPatterns(await patternsRes.json())
      if (assocRes.ok) setAssociations(await assocRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching data mining data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      frequent_itemset: 'bg-blue-100 text-blue-800',
      sequential: 'bg-purple-100 text-purple-800',
      clustering: 'bg-green-100 text-green-800',
      classification: 'bg-orange-100 text-orange-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Data Mining</h1>
          <p className="mt-2 text-gray-600">Pattern discovery and association rule mining</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Patterns</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_patterns}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Associations</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.total_associations}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Confidence</p>
              <p className="text-2xl font-bold text-green-600">{(statistics.avg_confidence * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Transactions</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.total_transactions.toLocaleString()}</p>
            </div>
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('patterns')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'patterns' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Patterns ({patterns.length})
            </button>
            <button
              onClick={() => setActiveTab('associations')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'associations' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Associations ({associations.length})
            </button>
          </nav>
        </div>

        {activeTab === 'patterns' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {patterns.map((pattern) => (
              <div key={pattern.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{pattern.name}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(pattern.type)}`}>
                    {pattern.type.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-4">{pattern.description}</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-500">Support</p>
                    <p className="text-lg font-bold text-blue-600">{(pattern.support * 100).toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Confidence</p>
                    <p className="text-lg font-bold text-green-600">{(pattern.confidence * 100).toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Lift</p>
                    <p className="text-lg font-bold text-purple-600">{pattern.lift.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'associations' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Antecedent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consequent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Support</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lift</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {associations.map((assoc) => (
                  <tr key={assoc.id}>
                    <td className="px-6 py-4 text-gray-900">{assoc.antecedent.join(', ')}</td>
                    <td className="px-6 py-4 text-gray-900 font-medium">{assoc.consequent.join(', ')}</td>
                    <td className="px-6 py-4 text-gray-500">{(assoc.support * 100).toFixed(1)}%</td>
                    <td className="px-6 py-4 text-green-600 font-medium">{(assoc.confidence * 100).toFixed(1)}%</td>
                    <td className="px-6 py-4 text-purple-600 font-medium">{assoc.lift.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
